import {
  Component,
  ViewChild,
  Input,
  Output,
  EventEmitter,
  OnInit,
  AfterViewInit,
  OnDestroy,
  OnChanges,
  SimpleChanges,
  ElementRef,
  HostListener,
  ChangeDetectorRef,
} from '@angular/core';
import { LocalizationService } from 'src/app/shared/services/localization.service';
import { Location } from '@angular/common';
import { ToastrService } from 'ngx-toastr';
import { TranslateService } from '@ngx-translate/core';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { Router } from '@angular/router';
import { AuthService } from 'src/app/modules/auth/services/auth.service';
import html2pdf from 'html2pdf.js';

export type DocumentOperationType = 'print' | 'export';
export type DocumentType =
  | 'receipt-voucher'
  | 'payment-voucher'
  | 'invoice'
  | 'general';

export interface DocumentData {
  serialNumber?: string;
  voucherNumber?: string;
  voucherDate?: string;
  journalDate?: string;
  refNumber?: string;
  currency?: any;
  exchangeRate?: number;
  accountDto?: any;
  cashBox?: any;
  paymentType?: string;
  paymentMethod?: string;
  isHold?: boolean;
  isPosted?: boolean;
  items?: any[];
  payers?: any[];
  beneficiaries?: any[];
  totalAmount?: number;
  description?: string;
  note?: string;
  [key: string]: any;
}

@Component({
  selector: 'app-print-export-document',
  templateUrl: './print-export-document.html',
  styleUrl: './print-export-document.css',
})
export class PrintExportDocumentComponent
  implements OnInit, AfterViewInit, OnDestroy, OnChanges
{
  @ViewChild('invoice', { static: false })
  invoiceEl!: ElementRef<HTMLDivElement>;
  @ViewChild('zoomContainer', { static: false })
  zoomContainerEl!: ElementRef<HTMLDivElement>;

  // Inputs for making the component reusable
  @Input() documentData: DocumentData | null = null;
  @Input() operationType: DocumentOperationType = 'print';
  @Input() documentType: DocumentType = 'general';
  @Input() titleTranslationKey: string = 'RECEIPT_VOUCHER.GENERAL.TITLE';
  @Input() autoExecute: boolean = false; // Auto execute operation on init
  @Input() showToolbar: boolean = true; // Show/hide toolbar
  @Input() returnUrl: string = '/'; // URL to return to after export
  @Input() isEmbedded: boolean = false; // When true, prevents navigation back and suppresses export notifications

  @Output() onComplete = new EventEmitter<void>();

  currentScale: number = 1;
  document: DocumentData | null = null;
  loading = false;
  currentLang: string = 'ar';
  private destroy$ = new Subject<void>();
  isExporting = false;

  private shouldToast(): boolean {
    return !this.isEmbedded;
  }

  private notifyError(translationKey: string, fallback: string): void {
    if (this.shouldToast()) {
      this.toastr.error(
        this.translateService.instant(translationKey) || fallback
      );
    } else {
      console.error(fallback);
    }
  }

  constructor(
    private location: Location,
    private localizationService: LocalizationService,
    private toastr: ToastrService,
    private translateService: TranslateService,
    private cdr: ChangeDetectorRef,
    private router: Router,
    private authService: AuthService
  ) {
    this.localizationService.currentLanguage$
      .pipe(takeUntil(this.destroy$))
      .subscribe((lang) => {
        this.currentLang = lang;
      });
  }

  ngOnInit(): void {
    // Try to get data from router state first
    const navigation = this.router.getCurrentNavigation();
    const state =
      navigation?.extras?.state ||
      (history.state && history.state.documentData ? history.state : null);

    if (state && state.documentData) {
      this.documentData = state.documentData;
      this.operationType = state.operationType || 'print';
      this.documentType = state.documentType || 'general';
      this.titleTranslationKey =
        state.titleTranslationKey || 'RECEIPT_VOUCHER.GENERAL.TITLE';
      this.autoExecute = state.autoExecute || false;
      this.showToolbar = state.showToolbar !== false;
      this.returnUrl = state.returnUrl || '/';
    }

    if (this.documentData) {
      this.document = this.normalizeDocumentData(this.documentData);
      this.loading = false;

      // Auto execute operation if requested
      if (this.autoExecute) {
        if (this.operationType === 'print') {
          this.onPrint();
        } else if (this.operationType === 'export') {
          this.onExport();
        }
      }
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['documentData'] && changes['documentData'].currentValue) {
      const newData = changes['documentData'].currentValue;
      this.documentData = newData;
      this.document = this.normalizeDocumentData(newData);
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  ngAfterViewInit(): void {
    setTimeout(() => this.fitToScreen(), 10);
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private normalizeDocumentData(data: DocumentData): DocumentData {
    const normalized = { ...data };

    normalized.items = data.items || data.payers || data.beneficiaries || [];

    return normalized;
  }

  @HostListener('window:resize')
  onWindowResize() {
    this.fitToScreen();
  }

  zoomIn(): void {
    if (!this.invoiceEl) return;
    this.currentScale += 0.1;
    this.applyScale();
  }

  zoomOut(): void {
    if (!this.invoiceEl) return;
    if (this.currentScale > 0.1) {
      this.currentScale -= 0.1;
      this.applyScale();
    }
  }

  fitToScreen(): void {
    if (!this.invoiceEl) return;
    const invoiceWidthPx = this.invoiceEl.nativeElement.offsetWidth;
    const containerWidthPx = this.zoomContainerEl.nativeElement.clientWidth;
    const scaleFactor = containerWidthPx / invoiceWidthPx;
    this.currentScale = scaleFactor > 1 ? 1 : scaleFactor;
    this.applyScale();
  }

  applyScale(): void {
    const invoiceDiv = this.invoiceEl.nativeElement;
    invoiceDiv.style.transform = `scale(${this.currentScale})`;
  }

  onPrint(): void {
    window.print();
  }

  onExport(): void {
    if (!this.document) {
      this.notifyError('COMMON.ERROR.NO_DATA', 'No export data available.');
      return;
    }

    if (!this.invoiceEl?.nativeElement) {
      this.notifyError(
        'COMMON.ERROR.PDF_GENERATION_FAILED',
        'Unable to locate export content.'
      );
      return;
    }

    this.isExporting = true;
    this.cdr.detectChanges();

    // Use requestAnimationFrame for better performance than setTimeout
    requestAnimationFrame(() => {
      const filename = this.generateFilename();
      const element = this.invoiceEl.nativeElement;

      const options = {
        margin: 0,
        filename,
        image: { type: 'jpeg', quality: 0.98 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: true,
        },
        jsPDF: {
          unit: 'mm',
          format: 'a4',
          orientation: 'portrait',
        },
      } as any;

      html2pdf()
        .from(element)
        .set(options)
        .save()
        .catch((error: any) => {
          console.error('HTML to PDF export error:', error);
          this.notifyError(
            'COMMON.ERROR.PDF_GENERATION_FAILED',
            'PDF generation failed.'
          );
        })
        .finally(() => {
          this.isExporting = false;
          this.cdr.detectChanges();
          this.onComplete.emit();

          if (!this.isEmbedded) {
            setTimeout(() => {
              this.goBack();
            }, 10);
          }
        });
    });
  }

  private formatNumber(value: any): string {
    if (value === null || value === undefined || value === '') {
      return '-';
    }
    const numberValue = Number(value);
    if (isNaN(numberValue)) {
      return value.toString();
    }
    return numberValue.toLocaleString(
      this.currentLang === 'ar' ? 'ar-EG' : 'en-US'
    );
  }

  goBack() {
    if (this.returnUrl && this.returnUrl !== '/') {
      this.router.navigate([this.returnUrl]);
    } else {
      this.location.back();
    }
  }

  getAccountName(account: any): string {
    if (!account) return '';
    return this.currentLang === 'ar'
      ? account.nameAr || account.name
      : account.nameEn || account.name;
  }

  getCurrencyName(currency: any): string {
    if (!currency) return '';
    return this.currentLang === 'ar'
      ? currency.nameAr || currency.currencyNameAr || currency.name
      : currency.nameEn || currency.currencyNameEn || currency.name;
  }

  public translateLabel(key: string): string {
    const prefix =
      this.documentType === 'payment-voucher'
        ? 'PAYMENT_VOUCHER'
        : 'RECEIPT_VOUCHER';
    const translationKey = `${prefix}.${key}`;
    const translated = this.translateService.instant(translationKey);
    if (translated && translated !== translationKey) {
      return translated;
    }
    const fallback = this.translateService.instant(key);
    if (fallback && fallback !== key) {
      return fallback;
    }
    return key;
  }

  public getPaymentTypeName(paymentType: any): string {
    if (!paymentType) return '';
    if (typeof paymentType === 'string') return paymentType;

    if (typeof paymentType === 'object') {
      if (this.currentLang === 'ar') {
        return (
          paymentType.nameAr ||
          paymentType.name ||
          paymentType.value?.toString() ||
          ''
        );
      }
      return (
        paymentType.nameEn ||
        paymentType.name ||
        paymentType.value?.toString() ||
        ''
      );
    }

    return paymentType.toString();
  }

  getTotals() {
    if (!this.document?.items) return { items: 0, total: 0 };
    const items = this.document.items.length;
    const localTotal = this.document.items.reduce(
      (acc: number, line: any) =>
        acc + (line.amount || 0) * (line.exchangeRate || 1),
      0
    );
    const masterRate =
      this.document.exchangeRate || this.document.currency?.exchangeRate || 1;
    const total = localTotal / masterRate;
    return { items, total };
  }

  getSerialNumberPart(document: any): string {
    const serialNumber = document?.serialNumber || document?.voucherNumber;
    if (!serialNumber) return '';
    const parts = serialNumber.split('-');
    return parts.length > 0 ? parts[parts.length - 1] : serialNumber;
  }

  /**
   * Generate appropriate filename based on document type
   */
  private generateFilename(): string {
    const serialNumber =
      this.document?.serialNumber || this.document?.voucherNumber || 'document';
    const timestamp = new Date().getTime();

    switch (this.documentType) {
      case 'receipt-voucher':
        return `receipt-voucher-${serialNumber}-${timestamp}.pdf`;
      case 'payment-voucher':
        return `payment-voucher-${serialNumber}-${timestamp}.pdf`;
      case 'invoice':
        return `invoice-${serialNumber}-${timestamp}.pdf`;
      default:
        return `document-${serialNumber}-${timestamp}.pdf`;
    }
  }

  /**
   * Public method to update document data dynamically
   */
  public updateDocumentData(data: DocumentData): void {
    this.document = this.normalizeDocumentData(data);
    this.loading = false;
    this.cdr.detectChanges();
    setTimeout(() => this.fitToScreen(), 10);
  }

  /**
   * Public method to execute operation programmatically
   */
  public executeOperation(
    type: DocumentOperationType = this.operationType
  ): void {
    if (type === 'print') {
      this.onPrint();
    } else if (type === 'export') {
      this.onExport();
    }
  }
  printDate: Date = new Date();

  getPrintedBy(): string {
    try {
      // 1. Try sessionStorage (userProfile) - Primary Source
      const userProfileString = sessionStorage.getItem('userProfile');
      if (userProfileString) {
        const userProfile = JSON.parse(userProfileString);
        if (this.currentLang === 'ar' && userProfile.fullNameAr)
          return userProfile.fullNameAr;
        if (this.currentLang !== 'ar' && userProfile.fullNameEn)
          return userProfile.fullNameEn;

        if (userProfile.firstName || userProfile.lastName) {
          return `${userProfile.firstName || ''} ${
            userProfile.lastName || ''
          }`.trim();
        }
      }

      // 2. Try AuthService
      const currentUser = this.authService.currentUserValue;
      if (currentUser) {
        const userAny = currentUser as any;
        if (this.currentLang === 'ar' && userAny.fullNameAr)
          return userAny.fullNameAr;
        if (this.currentLang !== 'ar' && userAny.fullNameEn)
          return userAny.fullNameEn;

        if (currentUser.firstName || currentUser.lastName) {
          return `${currentUser.firstName || ''} ${
            currentUser.lastName || ''
          }`.trim();
        }
      }

      // 3. Try localStorage (user) - Fallback
      const userLocal = localStorage.getItem('user');
      if (userLocal) {
        const parsedUser = JSON.parse(userLocal);
        return this.currentLang === 'ar'
          ? parsedUser.fullNameAr || parsedUser.fullName
          : parsedUser.fullNameEn || parsedUser.fullName;
      }

      return '';
    } catch (error) {
      console.warn('Error getting printed by user:', error);
      return '';
    }
  }
}
